import { Menu, Transition } from '@headlessui/react';
import React, { Fragment } from 'react';
import { classNames } from '@/lib/classNames';

interface MenuItemProps {
  onClick: React.MouseEventHandler<HTMLAnchorElement>;
  text: string;
}

const MenuItem = ({ onClick, text }: MenuItemProps) => {
  return (
    <Menu.Item>
      {({ active }) => (
        <a
          className={`block px-4 py-2  text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
            active && 'bg-gray-200'
          }`}
          role="menuitem"
          onClick={onClick}
        >
          {text}
        </a>
      )}
    </Menu.Item>
  );
};

type PopupDirection = 'down' | 'up';
interface OptionsMenuProps {
  editMenuItems: MenuItemProps[];
  asButton?: boolean;
  popupDirection?: PopupDirection;
}
const popupDirectionStyles: Record<PopupDirection, string> = {
  down: 'right-0 mt-2',
  up: 'right-0 bottom-0 mb-12'
};

export const OptionsMenu = ({
  editMenuItems,
  asButton = false,
  popupDirection = 'down'
}: OptionsMenuProps) => {
  return (
    <Menu as="div" className="flex items-center">
      {({ open }) => (
        <>
          <div className="relative inline-block text-left">
            <div className="flex items-center">
              <Menu.Button
                className={classNames(
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                  asButton &&
                    'rounded border border-highlight/20 bg-white p-3 text-base font-bold text-tertiary hover:bg-gray-100'
                )}
              >
                <div
                  className="flex items-center text-gray-800 hover:text-gray-600"
                  id="options-menu"
                  aria-haspopup="true"
                  aria-expanded="true"
                >
                  <span className="sr-only">Open options</span>
                  <svg
                    className="h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                  </svg>
                </div>
              </Menu.Button>
            </div>
            <Transition
              as={Fragment}
              show={open}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-100"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-100"
            >
              <Menu.Items
                static
                className={classNames(
                  'absolute z-10 w-56 origin-top-right cursor-pointer rounded-md border border-gray-200 bg-white shadow-lg outline-none',
                  popupDirectionStyles[popupDirection]
                )}
                aria-orientation="vertical"
                aria-labelledby="options-menu"
              >
                {editMenuItems.map((item: MenuItemProps, index: number) => {
                  return (
                    <MenuItem
                      text={item.text}
                      onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                        if (item.onClick) {
                          item.onClick(event);
                        }
                        event.stopPropagation();
                      }}
                      key={index}
                    />
                  );
                })}
              </Menu.Items>
            </Transition>
          </div>
        </>
      )}
    </Menu>
  );
};

export default OptionsMenu;
